// components/AssignmentModal.jsx
import { Fragment, useEffect, useMemo, useState, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
  UsersThree,
  X,
  CircleNotch,
  MagnifyingGlass,
  Check,
  ArrowsLeftRight,
  Plus,
  Minus,
  Shield,
  WarningCircle,
  UserCircle,
  ArrowClockwise,
} from "phosphor-react";
import clientsService from "../../services/clientsService";
import assignmentsService from "../../services/assignmentsService";
import toast from "react-hot-toast";

const cls = (...a) => a.filter(Boolean).join(" ");

// ---------- tiny UI primitives ----------
function Chip({ children, className = "" }) {
  return (
    <span
      className={cls(
        "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium",
        "bg-neutral-100 text-neutral-700",
        "dark:bg-neutral-900/40 dark:text-neutral-300",
        className
      )}
    >
      {children}
    </span>
  );
}

function SoftButton({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={cls(
        "inline-flex items-center justify-center gap-2 h-9 rounded-xl px-3 text-sm font-medium",
        "border border-neutral-200 bg-white text-neutral-900",
        "hover:bg-neutral-50",
        "dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:hover:bg-neutral-800",
        "transition-colors",
        "disabled:opacity-60 disabled:cursor-not-allowed",
        className
      )}
    >
      {children}
    </button>
  );
}

/** CustomCheckbox: accessible, keyboardable, animated check */
function CustomCheckbox({ checked, onChange, label, subLabel, className = "", ...rest }) {
  return (
    <label
      className={cls(
        "group flex items-center justify-between gap-3 px-4 py-2",
        "cursor-pointer border-b last:border-b-0 border-neutral-100",
        "hover:bg-neutral-50/70",
        "dark:border-neutral-900 dark:bg-neutral-950 dark:hover:bg-neutral-900/70",
        className
      )}
    >
      <div className="flex items-center gap-3">
        <span
          role="checkbox"
          aria-checked={checked}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === " " || e.key === "Enter") {
              e.preventDefault();
              onChange?.(!checked);
            }
          }}
          onClick={() => onChange?.(!checked)}
          className={cls(
            "relative inline-flex h-5 w-5 items-center justify-center rounded-lg border shadow-sm",
            "transition-colors",
            checked
              ? "bg-neutral-900 border-neutral-900 dark:bg-neutral-100 dark:border-neutral-100"
              : "bg-white border-neutral-300 dark:bg-neutral-950 dark:border-neutral-700"
          )}
          {...rest}
        >
          <Check
            size={14}
            weight="bold"
            className={cls(
              "transition-transform duration-150",
              checked
                ? "scale-100 text-white dark:text-neutral-900"
                : "scale-0 text-transparent"
            )}
          />
        </span>

        <div className="min-w-0">
          <div className="text-sm font-medium text-neutral-900 dark:text-neutral-50 truncate">
            {label}
          </div>
          {subLabel && (
            <div className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate">
              {subLabel}
            </div>
          )}
        </div>
      </div>
    </label>
  );
}

// ---------- main ----------
export default function AssignmentModal({ isOpen, onClose, manager, onChanged }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [allClients, setAllClients] = useState([]); // [{_id, fullName, email}]
  const [assigned, setAssigned] = useState([]); // array of client IDs
  const [initialAssigned, setInitialAssigned] = useState([]);

  const [qLeft, setQLeft] = useState("");
  const [qRight, setQRight] = useState("");
  const [selLeft, setSelLeft] = useState(new Set());
  const [selRight, setSelRight] = useState(new Set());

  const managerId = manager?._id;

  // fetchers
  const fetchAllClients = useCallback(async () => {
    const res = await clientsService.searchClients();
    if (res?.data?.statusCode === 200) {
      setAllClients(
        (res.data.data || []).map((c) => ({
          _id: c._id,
          fullName: c.fullName,
          email: c.email,
          reference: c.reference,
        }))
      );
    } else {
      throw new Error("clients fetch");
    }
  }, []);

  const fetchAssignedClients = useCallback(async (mgrId) => {
    const res = await assignmentsService.getManagerClients(mgrId, { active: true });
    if (res?.data?.ok) {
      return (res.data.items || []).map((i) => i?.client?._id).filter(Boolean);
    }
    throw new Error("assigned clients fetch");
  }, []);

  useEffect(() => {
    if (!isOpen || !managerId) return;
    setLoading(true);
    Promise.all([fetchAllClients(), fetchAssignedClients(managerId)])
      .then(([, a]) => {
        setAssigned(a);
        setInitialAssigned(a);
      })
      .catch(() => toast.error(t("Failed to load assignments")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, managerId]);

  // lists
  const leftList = useMemo(() => {
    const setA = new Set(assigned);
    const base = allClients.filter((c) => !setA.has(c._id));
    if (!qLeft.trim()) return base;
    const q = qLeft.trim().toLowerCase();
    return base.filter(
      (c) =>
        (c.fullName || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q)
    );
  }, [allClients, assigned, qLeft]);

  const rightList = useMemo(() => {
    const map = new Map(allClients.map((c) => [c._id, c]));
    const base = assigned.map((id) => map.get(id)).filter(Boolean);
    if (!qRight.trim()) return base;
    const q = qRight.trim().toLowerCase();
    return base.filter(
      (c) =>
        (c.fullName || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q)
    );
  }, [allClients, assigned, qRight]);

  // selection helpers
  const onToggleLeft = (id) => {
    const next = new Set(selLeft);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelLeft(next);
  };
  const onToggleRight = (id) => {
    const next = new Set(selRight);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelRight(next);
  };

  const selectAllLeft = () => {
    setSelLeft(new Set(leftList.map((c) => c._id)));
  };
  const clearLeft = () => setSelLeft(new Set());

  const selectAllRight = () => {
    setSelRight(new Set(rightList.map((c) => c._id)));
  };
  const clearRight = () => setSelRight(new Set());

  // move
  const moveRight = () => {
    if (selLeft.size === 0) return;
    const setA = new Set(assigned);
    selLeft.forEach((id) => setA.add(id));
    setAssigned([...setA]);
    setSelLeft(new Set());
    setQLeft(""); // clear search to reveal updated list
  };

  const moveLeft = () => {
    if (selRight.size === 0) return;
    const remove = new Set(selRight);
    setAssigned((prev) => prev.filter((id) => !remove.has(id)));
    setSelRight(new Set());
    setQRight("");
  };

  // persist
  const onSave = async () => {
    if (!managerId) return;
    try {
      setSaving(true);
      const initial = new Set(initialAssigned);
      const now = new Set(assigned);

      const added = [...now].filter((x) => !initial.has(x));
      const removed = [...initial].filter((x) => !now.has(x));

      const tasks = [];
      if (added.length)
        tasks.push(
          assignmentsService.assign({ managerId, clientIds: added })
        );
      if (removed.length)
        tasks.push(
          assignmentsService.unassign({ managerId, clientIds: removed })
        );
      if (tasks.length) await Promise.all(tasks);

      setInitialAssigned(assigned);
      toast.success(t("Assignments updated"));
      onChanged?.();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || t("Failed to save assignments"));
    } finally {
      setSaving(false);
    }
  };

  const onRefresh = async () => {
    if (!managerId) return;
    try {
      setLoading(true);
      await fetchAllClients();
      const cur = await fetchAssignedClients(managerId);
      setAssigned(cur);
      setInitialAssigned(cur);
      setSelLeft(new Set());
      setSelRight(new Set());
      setQLeft("");
      setQRight("");
    } catch {
      toast.error(t("Failed to refresh"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-neutral-950">
                {/* Header */}
                <div className="border-b border-neutral-200 bg-neutral-50/70 px-5 py-4 dark:border-neutral-900 dark:bg-neutral-900/50">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900">
                        <UsersThree size={18} weight="bold" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <Dialog.Title className="text-base font-semibold text-neutral-900 dark:text-white">
                          {t("Assign Clients")}
                        </Dialog.Title>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-600 dark:text-neutral-400">
                          <Chip>
                            {manager?.role?.name}:{" "}
                            <span className="font-semibold ml-1">
                              {manager?.fullName || "-"}
                            </span>
                          </Chip>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <SoftButton onClick={onRefresh} aria-label={t("Refresh")}>
                        <ArrowClockwise size={16} weight="bold" />
                        <span className="hidden sm:inline">{t("Refresh")}</span>
                      </SoftButton>
                      <button
                        onClick={onClose}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
                        aria-label={t("Close")}
                      >
                        <X size={18} weight="bold" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Manager meta */}
                <div className="px-5 pt-3">
                  <div className="flex flex-wrap items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700 dark:border-neutral-900 dark:bg-neutral-950 dark:text-neutral-300">
                    <Shield size={16} weight="bold" className="text-neutral-500 dark:text-neutral-400" />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{manager?.role?.name}  {t(" Email")}:</span>
                      <span className="text-neutral-500 dark:text-neutral-400">
                        {manager?.email || "-"}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 ml-auto">
                      <Chip>
                        {t("Assigned")}: <span className="ml-1 font-semibold">{assigned.length}</span>
                      </Chip>
                      <Chip>
                        {t("Available")}:{" "}
                        <span className="ml-1 font-semibold">
                          {Math.max(allClients.length - assigned.length, 0)}
                        </span>
                      </Chip>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  {loading ? (
                    <div className="flex h-56 flex-col items-center justify-center gap-3 text-sm text-neutral-500 dark:text-neutral-400">
                      <CircleNotch className="animate-spin" size={28} weight="bold" />
                      {t("Loading...")}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                      {/* Available */}
                      <div className="flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white dark:border-neutral-900 dark:bg-neutral-950">
                        {/* sticky subheader */}
                        <div className="sticky top-0 z-10 border-b border-neutral-200 bg-white px-4 py-3 dark:border-neutral-900 dark:bg-neutral-950">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <WarningCircle
                                size={18}
                                weight="bold"
                                className="text-neutral-500 dark:text-neutral-400"
                              />
                              <div className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                                {t("Available Clients")}
                              </div>
                              <Chip>{leftList.length}</Chip>
                            </div>

                            <div className="flex items-center gap-2">
                              <SoftButton onClick={selectAllLeft} disabled={!leftList.length}>
                                {t("Select All")}
                              </SoftButton>
                              <SoftButton onClick={clearLeft} disabled={!selLeft.size}>
                                {t("Clear")}
                              </SoftButton>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-2 py-1.5 text-sm dark:border-neutral-800 dark:bg-neutral-900">
                            <MagnifyingGlass
                              size={16}
                              weight="bold"
                              className="text-neutral-400 dark:text-neutral-500"
                            />
                            <input
                              value={qLeft}
                              onChange={(e) => setQLeft(e.target.value)}
                              placeholder={t("Search")}
                              className="h-7 w-full bg-transparent text-xs text-neutral-800 placeholder:text-neutral-400 focus:outline-none dark:text-neutral-100 dark:placeholder:text-neutral-500"
                            />
                          </div>
                        </div>

                        {/* list */}
                        <div className="h-64 max-h-80 overflow-auto">
                          {leftList.length === 0 ? (
                            <div className="flex h-full items-center justify-center px-6 text-sm text-neutral-500 dark:text-neutral-400">
                              <div className="flex items-center gap-2">
                                <UserCircle size={18} weight="bold" />
                                <span>{t("No items")}</span>
                              </div>
                            </div>
                          ) : (
                            leftList.map((c) => (
                              <CustomCheckbox
                                key={c._id}
                                checked={selLeft.has(c._id)}
                                onChange={() => onToggleLeft(c._id)}
                                label={c.reference || c.fullName}
                                subLabel={c.email}
                              />
                            ))
                          )}
                        </div>

                        {/* action */}
                        <div className="border-t border-neutral-200 bg-neutral-50/80 px-3 py-3 dark:border-neutral-900 dark:bg-neutral-950">
                          <button
                            type="button"
                            onClick={moveRight}
                            disabled={selLeft.size === 0}
                            className={cls(
                              "inline-flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition",
                              selLeft.size === 0
                                ? "cursor-not-allowed bg-neutral-200 text-neutral-500 dark:bg-neutral-900 dark:text-neutral-500"
                                : "bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200"
                            )}
                          >
                            <Plus size={16} weight="bold" />
                            {t("Assign Selected")}
                          </button>
                        </div>
                      </div>

                      {/* Assigned */}
                      <div className="relative flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-white dark:border-neutral-900 dark:bg-neutral-950">
                        <div className="sticky top-0 z-10 border-b border-neutral-200 bg-white px-4 py-3 dark:border-neutral-900 dark:bg-neutral-950">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <ArrowsLeftRight
                                size={18}
                                weight="bold"
                                className="text-neutral-500 dark:text-neutral-400"
                              />
                              <div className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                                {t("Assigned to")} {manager?.role?.name}
                              </div>
                              <Chip>{rightList.length}</Chip>
                            </div>

                            <div className="flex items-center gap-2">
                              <SoftButton onClick={selectAllRight} disabled={!rightList.length}>
                                {t("Select All")}
                              </SoftButton>
                              <SoftButton onClick={clearRight} disabled={!selRight.size}>
                                {t("Clear")}
                              </SoftButton>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-2 py-1.5 text-sm dark:border-neutral-800 dark:bg-neutral-900">
                            <MagnifyingGlass
                              size={16}
                              weight="bold"
                              className="text-neutral-400 dark:text-neutral-500"
                            />
                            <input
                              value={qRight}
                              onChange={(e) => setQRight(e.target.value)}
                              placeholder={t("Search")}
                              className="h-7 w-full bg-transparent text-xs text-neutral-800 placeholder:text-neutral-400 focus:outline-none dark:text-neutral-100 dark:placeholder:text-neutral-500"
                            />
                          </div>
                        </div>

                        <div className="h-64 max-h-80 overflow-y-auto">
                          {rightList.length === 0 ? (
                            <div className="flex h-full items-center justify-center px-6 text-sm text-neutral-500 dark:text-neutral-400">
                              <div className="flex items-center gap-2">
                                <UserCircle size={18} weight="bold" />
                                <span>{t("No items")}</span>
                              </div>
                            </div>
                          ) : (
                            rightList.map((c) => (
                              <CustomCheckbox
                                key={c._id}
                                checked={selRight.has(c._id)}
                                onChange={() => onToggleRight(c._id)}
                                label={c.reference || c.fullName}
                                subLabel={c.email}
                              />
                            ))
                          )}
                        </div>

                        <div className="absolute inset-x-0 bottom-0 border-t border-neutral-200 bg-neutral-50/80 px-3 py-3 dark:border-neutral-900 dark:bg-neutral-950">
                          <SoftButton
                            onClick={moveLeft}
                            disabled={selRight.size === 0}
                            className={cls(
                              "w-full justify-center",
                              selRight.size === 0
                                ? ""
                                : "bg-white hover:bg-neutral-100 dark:bg-neutral-900 dark:hover:bg-neutral-800"
                            )}
                          >
                            <Minus size={16} weight="bold" />
                            {t("Unassign Selected")}
                          </SoftButton>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 border-t border-neutral-200 bg-neutral-50/70 px-5 py-4 dark:border-neutral-900 dark:bg-neutral-900/60">
                  <SoftButton onClick={onClose}>{t("Cancel")}</SoftButton>
                  <button
                    onClick={onSave}
                    disabled={saving || loading}
                    className={cls(
                      "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition",
                      "bg-neutral-900 text-white hover:bg-neutral-800",
                      "dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-neutral-200",
                      "disabled:opacity-60 disabled:cursor-not-allowed"
                    )}
                  >
                    {saving ? (
                      <>
                        <CircleNotch className="h-4 w-4 animate-spin" size={16} weight="bold" />
                        {t("Saving...")}
                      </>
                    ) : (
                      <>
                        <Check size={16} weight="bold" />
                        {t("Save Assignments")}
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
